import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, BookOpen, Clock, GraduationCap, Loader2, ListChecks, Play, AlertTriangle } from 'lucide-react';
import { useMoodleCourses } from '../../hooks/useMoodleHooks';
import { useEnsureJourneyMapsLoaded, useJourneyMaps } from '../../hooks/journey-map-hooks';
import { buildCourseDetailPath } from '../../support/moodleDetailRoutes';
import { useModuleImageUrl } from './hooks';
import { Module } from './types'
import { vidcastEmbedUrl } from './utils';
import { TextContentView } from './TextContentView';
import { moodleCourseIdFromUrl, youtubeIdFromContent } from './utils';

function cleanSummary(html?: string) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function ImageModule({ seriesId, module }: { seriesId?: number | string; module: Module }) {
  const content = module.content || {};
  const { url, isLoading, isError } = useModuleImageUrl(seriesId, module);

  return (
    <div className="aspect-video bg-[#f3f4f6] flex items-center justify-center">
      {isLoading ? (
        <Loader2 className="w-5 h-5 text-[#d1d5db] animate-spin" />
      ) : isError || !url ? (
        <div className="flex flex-col items-center gap-1.5 text-[#9ca3af] text-xs">
          <AlertTriangle className="w-4 h-4" />
          Couldn&apos;t load image
        </div>
      ) : (
        <img src={url} alt={content.alt || ''} className="max-w-full max-h-full object-contain" />
      )}
    </div>
  );
}

function MoodleModule({ module }: { module: Module }) {
  const content = module.content || {};
  const courseId = moodleCourseIdFromUrl(content.url);
  const { data: courses, isLoading } = useMoodleCourses({ enabled: !!courseId }, 'internal');
  const moodleCourse: any = courseId ? (courses || []).find((c) => String(c.id) === courseId) : undefined;

  if (courseId && isLoading) {
    return (
      <div className="aspect-video bg-[#f3f4f6] flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-[#d1d5db] animate-spin" />
      </div>
    );
  }

  const url: string = content.url || '';
  const target = courseId
    ? buildCourseDetailPath(courseId, { instance: 'internal', fullscreen: false })
    : url.startsWith('/')
    ? url
    : undefined;
  const buttonClass =
    'inline-flex items-center gap-1.5 px-4 py-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg font-medium text-xs transition';

  return (
    <div className="aspect-video bg-gradient-to-b from-[#f0fdfa] to-white flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-5 text-center">
        <div className="w-11 h-11 rounded-xl bg-[#f0fdfa] text-[#0d9488] flex items-center justify-center mx-auto mb-3">
          <GraduationCap className="w-5 h-5" />
        </div>
        <p className="font-semibold text-[#111827] text-sm mb-1">{moodleCourse?.fullname || module.title}</p>
        {(moodleCourse?.category?.name || moodleCourse?.categoryname) && (
          <p className="text-[11px] text-[#0d9488] font-medium uppercase tracking-wide mb-2">
            {moodleCourse?.category?.name || moodleCourse?.categoryname}
          </p>
        )}
        {moodleCourse?.summary && (
          <p className="text-xs text-[#6b7280] line-clamp-3 mb-4">{cleanSummary(moodleCourse.summary)}</p>
        )}
        {moodleCourse?.shortname && <p className="text-[11px] text-[#9ca3af] mb-4">{moodleCourse.shortname}</p>}
        {target ? (
          <Link to={target} className={buttonClass}>
            <GraduationCap className="w-3.5 h-3.5" /> Start Course
          </Link>
        ) : url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className={buttonClass}>
            <GraduationCap className="w-3.5 h-3.5" /> Start Course
          </a>
        ) : null}
      </div>
    </div>
  );
}

function JourneyMapModule({ module }: { module: Module }) {
  const content = module.content || {};
  useEnsureJourneyMapsLoaded();
  const maps = useJourneyMaps();
  const journeyMap = maps.find((jm) => String(jm.id) === String(content.id));
  const itemCount = Array.isArray(journeyMap?.journey_map_items) ? journeyMap!.journey_map_items!.length : null;
  const image = journeyMap?.background_image_url || journeyMap?.image_url;

  return (
    <div className="aspect-video bg-gradient-to-b from-[#ecfdf5] to-white flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-xl border border-[#e5e7eb] shadow-sm overflow-hidden text-center">
        {image ? <img src={image} alt="" className="w-full h-24 object-cover" /> : null}
        <div className="p-5">
          <div className="w-11 h-11 rounded-xl bg-[#ecfdf5] text-[#059669] flex items-center justify-center mx-auto mb-3">
            <BookOpen className="w-5 h-5" />
          </div>
          <p className="font-semibold text-[#111827] text-sm mb-1">{journeyMap?.name || module.title}</p>
          {journeyMap?.description && (
            <p className="text-xs text-[#6b7280] line-clamp-3 mb-2">{journeyMap.description}</p>
          )}
          <p className="flex items-center justify-center gap-3 text-[11px] text-[#9ca3af] mb-3">
            {!!journeyMap?.estimated_duration_minutes && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> ~{journeyMap.estimated_duration_minutes} min
              </span>
            )}
            {itemCount != null && (
              <span className="flex items-center gap-1">
                <ListChecks className="w-3 h-3" /> {itemCount} {itemCount === 1 ? 'step' : 'steps'}
              </span>
            )}
          </p>
          <Link
            to={`/enablement/journey-map/${content.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg font-medium text-xs transition"
          >
            <BookOpen className="w-3.5 h-3.5" /> View Full Journey Map
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ModuleViewer({
  module,
  seriesId,
  onViewed,
}: {
  module: Module;
  seriesId?: number | string;
  onViewed: () => void;
}) {
  useEffect(() => {
    onViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id]);

  const content = module.content || {};
  // Autoplay only when the author checked it; otherwise show a click-to-play poster.
  const [playing, setPlaying] = useState(!!content.autoplay);

  if (module.type === 'youtube') {
    const videoId = youtubeIdFromContent(content);
    if (!playing) {
      return (
        <div className="aspect-video bg-black relative">
          {videoId && (
            <img
              src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label="Play video"
            className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/35 transition-colors"
          >
            <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <Play className="w-7 h-7 text-[#dc2626] ml-1" fill="currentColor" />
            </span>
          </button>
        </div>
      );
    }
    return (
      <div className="aspect-video bg-black">
        <iframe
          title={module.title}
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          className="w-full h-full"
          allow="autoplay; encrypted-media"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    );
  }

  if (module.type === 'vidcast') {
    if (!playing) {
      return (
        <div className="aspect-video bg-black relative flex items-center justify-center">
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label="Play video"
            className="w-16 h-16 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-colors"
          >
            <Play className="w-7 h-7 text-[#9333ea] ml-1" fill="currentColor" />
          </button>
        </div>
      );
    }
    return (
      <div className="aspect-video bg-black">
        <iframe
          title={module.title}
          src={vidcastEmbedUrl(content.embedUrl)}
          className="w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (module.type === 'image') {
    return <ImageModule seriesId={seriesId} module={module} />;
  }

  if (module.type === 'text') {
    return (
      <div className="aspect-video bg-[#f9fafb] overflow-y-auto p-6">
        <TextContentView text={content.text || ''} format={content.format} />
      </div>
    );
  }

  if (module.type === 'mindtickle') {
    return (
      <div className="aspect-video bg-[#f3f4f6] flex items-center justify-center">
        <a
          href={content.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center gap-3 px-6 py-4 bg-white rounded-lg shadow hover:shadow-md transition border border-[#e5e7eb]"
        >
          <ExternalLink className="w-6 h-6 text-[#f97316]" />
          <span className="font-medium text-[#374151] text-sm">Open Mindtickle</span>
          <span className="text-xs text-[#6b7280]">Opens in new tab</span>
        </a>
      </div>
    );
  }

  if (module.type === 'moodle') return <MoodleModule module={module} />;

  if (module.type === 'journey_map') return <JourneyMapModule module={module} />;

  return <div className="aspect-video bg-[#e5e7eb]" />;
}
